import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Container, Group, Loader, Modal, Paper, Select,
  SimpleGrid, Stack, Table, Text, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPackage, IconTruckDelivery } from '@tabler/icons-react';
import { collection, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';

const BRANCHES = [
  { value: 'all', label: 'Todas las sucursales' },
  { value: 'bellas_artes', label: 'Bellas Artes' },
  { value: 'providencia', label: 'Providencia' },
  { value: 'nunoa', label: 'Ñuñoa' },
];

const STATUS = {
  paid: { label: 'Pagado', color: 'blue' },
  in_preparation: { label: 'En preparación', color: 'orange' },
  ready_for_pickup: { label: 'Listo para retiro', color: 'green' },
  delivered: { label: 'Entregado', color: 'gray' },
  awaiting_payment: { label: 'Esperando pago', color: 'yellow' },
  blocked: { label: 'Bloqueado', color: 'red' },
};

const NEXT_STATUS = {
  paid: { value: 'in_preparation', label: 'Comenzar preparación' },
  in_preparation: { value: 'ready_for_pickup', label: 'Marcar listo para retiro' },
  ready_for_pickup: { value: 'delivered', label: 'Marcar entregado' },
};

const money = (value) => `$${Number(value || 0).toLocaleString('es-CL')}`;
const dateText = (value) => {
  if (!value) return 'Sin fecha';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? 'Sin fecha' : date.toLocaleString('es-CL');
};

export default function ProductOrders() {
  const { currentUser, userDoc, isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [branchFilter, setBranchFilter] = useState(isAdmin ? 'all' : userDoc?.sucursalId || 'all');

  useEffect(() => {
    const ordersQuery = isAdmin
      ? query(collection(db, 'productOrders'))
      : query(collection(db, 'productOrders'), where('pickup.branchId', '==', userDoc?.sucursalId || '__none__'));
    return onSnapshot(ordersQuery, (snapshot) => {
      setOrders(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      }));
      setLoading(false);
      setError('');
    }, (snapshotError) => {
      console.error('Error cargando pedidos:', snapshotError);
      setError('No se pudieron cargar los pedidos de la tienda.');
      setLoading(false);
    });
  }, [isAdmin, userDoc?.sucursalId]);

  const visibleOrders = useMemo(() => orders.filter((order) => {
    const matchesBranch = branchFilter === 'all' || order.pickup?.branchId === branchFilter;
    const fulfillment = order.fulfillmentStatus || 'awaiting_payment';
    const matchesStatus = statusFilter === 'all'
      || (statusFilter === 'active' && ['paid', 'in_preparation', 'ready_for_pickup'].includes(fulfillment))
      || fulfillment === statusFilter;
    return matchesBranch && matchesStatus;
  }), [orders, branchFilter, statusFilter]);

  const stats = useMemo(() => ({
    paid: orders.filter((order) => order.fulfillmentStatus === 'paid').length,
    preparing: orders.filter((order) => order.fulfillmentStatus === 'in_preparation').length,
    ready: orders.filter((order) => order.fulfillmentStatus === 'ready_for_pickup').length,
  }), [orders]);

  const advanceOrder = async (order) => {
    const next = NEXT_STATUS[order.fulfillmentStatus];
    if (!next || order.status === 'refunded' || order.status === 'charged_back') return;
    setUpdating(order.id);
    try {
      await updateDoc(doc(db, 'productOrders', order.id), {
        fulfillmentStatus: next.value,
        fulfillmentUpdatedAt: serverTimestamp(),
        fulfillmentUpdatedBy: currentUser.uid,
        fulfillmentUpdatedByEmail: currentUser.email || '',
      });
      notifications.show({ title: 'Pedido actualizado', message: `Nuevo estado: ${STATUS[next.value].label}`, color: 'green' });
      setSelected((current) => current?.id === order.id ? { ...current, fulfillmentStatus: next.value } : current);
    } catch (updateError) {
      console.error('Error actualizando pedido:', updateError);
      notifications.show({ title: 'No se pudo actualizar', message: updateError.message, color: 'red' });
    } finally {
      setUpdating('');
    }
  };

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <div><Title order={1}>Pedidos tienda</Title><Text c="dimmed">Productos pagados para preparar y entregar en sucursal.</Text></div>
        <IconPackage size={34} />
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="lg">
        <Card withBorder><Text size="sm" c="dimmed">Pagados</Text><Title order={2}>{stats.paid}</Title></Card>
        <Card withBorder><Text size="sm" c="dimmed">En preparación</Text><Title order={2}>{stats.preparing}</Title></Card>
        <Card withBorder><Text size="sm" c="dimmed">Listos para retiro</Text><Title order={2}>{stats.ready}</Title></Card>
      </SimpleGrid>

      <Paper withBorder p="md" mb="lg">
        <Group align="end">
          <Select label="Estado" value={statusFilter} onChange={(value) => setStatusFilter(value || 'active')} data={[
            { value: 'active', label: 'Pendientes de entrega' }, { value: 'all', label: 'Todos' },
            ...Object.entries(STATUS).map(([value, meta]) => ({ value, label: meta.label })),
          ]} />
          {isAdmin && <Select label="Sucursal" value={branchFilter} onChange={(value) => setBranchFilter(value || 'all')} data={BRANCHES} />}
        </Group>
      </Paper>

      {error && <Alert icon={<IconAlertCircle size={18} />} color="red" mb="lg">{error}</Alert>}
      <Paper withBorder>
        {loading ? <Group justify="center" p="xl"><Loader /></Group> : (
          <Table.ScrollContainer minWidth={900}>
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Thead><Table.Tr><Table.Th>Pedido</Table.Th><Table.Th>Cliente / retiro</Table.Th><Table.Th>Productos</Table.Th><Table.Th>Sucursal</Table.Th><Table.Th>Total</Table.Th><Table.Th>Estado</Table.Th><Table.Th /></Table.Tr></Table.Thead>
              <Table.Tbody>
                {visibleOrders.length === 0 ? <Table.Tr><Table.Td colSpan={7}><Text ta="center" c="dimmed" py="xl">No hay pedidos para este filtro.</Text></Table.Td></Table.Tr> : visibleOrders.map((order) => {
                  const fulfillment = order.fulfillmentStatus || 'awaiting_payment';
                  const meta = STATUS[fulfillment] || STATUS.awaiting_payment;
                  const next = NEXT_STATUS[fulfillment];
                  return <Table.Tr key={order.id}>
                    <Table.Td><Text fw={800}>{order.orderNumber || order.id}</Text><Text size="xs" c="dimmed">{dateText(order.createdAt)}</Text></Table.Td>
                    <Table.Td><Text fw={700}>{order.customer?.name}</Text><Text size="xs">Retira: {order.pickup?.name}</Text><Text size="xs" c="dimmed">{order.pickup?.phone}</Text></Table.Td>
                    <Table.Td>{(order.items || []).map((item) => <Text key={item.productId} size="sm">{item.name} x{item.quantity}</Text>)}</Table.Td>
                    <Table.Td>{order.pickup?.branchName || order.pickup?.branchId}</Table.Td>
                    <Table.Td><Text fw={800}>{money(order.total)}</Text></Table.Td>
                    <Table.Td><Badge color={meta.color} variant="light">{meta.label}</Badge>{['refunded', 'charged_back'].includes(order.status) && <Badge color="red" mt={4}>Pago bloqueado</Badge>}</Table.Td>
                    <Table.Td><Group justify="flex-end" wrap="nowrap"><Button size="xs" variant="light" onClick={() => setSelected(order)}>Ver</Button>{next && !['refunded', 'charged_back'].includes(order.status) && <Button size="xs" loading={updating === order.id} onClick={() => advanceOrder(order)}>{next.label}</Button>}</Group></Table.Td>
                  </Table.Tr>;
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Paper>

      <Modal opened={Boolean(selected)} onClose={() => setSelected(null)} title={selected?.orderNumber || 'Detalle del pedido'} size="lg">
        {selected && <Stack>
          <Group justify="space-between"><Badge color={(STATUS[selected.fulfillmentStatus] || STATUS.awaiting_payment).color}>{(STATUS[selected.fulfillmentStatus] || STATUS.awaiting_payment).label}</Badge><Text fw={800}>{money(selected.total)}</Text></Group>
          <Card withBorder><Text fw={800}>Comprador</Text><Text>{selected.customer?.name}</Text><Text size="sm">{selected.customer?.email}</Text><Text size="sm">{selected.customer?.phone}</Text></Card>
          <Card withBorder><Text fw={800}>Retiro</Text><Text>{selected.pickup?.name} · {selected.pickup?.phone}</Text><Text size="sm">{selected.pickup?.branchName}</Text></Card>
          <Card withBorder><Text fw={800} mb="xs">Productos</Text>{(selected.items || []).map((item) => <Group key={item.productId} justify="space-between"><Text>{item.name} x{item.quantity}</Text><Text>{money(item.lineTotal)}</Text></Group>)}</Card>
          {(selected.items || []).some((item) => item.includedPrepaidServices?.length) && <Card withBorder bg="yellow.0">
            <Text fw={800} mb="xs">Servicios prepagados incluidos</Text>
            {(selected.items || []).flatMap((item) => (item.includedPrepaidServices || []).map((service) => (
              <Text key={`${item.productId}-${service.service}`} size="sm">{service.service} x{Number(service.quantity || 1) * Number(item.quantity || 1)} · válido en todas las sucursales</Text>
            )))}
            <Text size="xs" c="dimmed" mt="xs">IDs: {(selected.prepaidServiceIds || []).join(', ') || 'Se crearán al confirmarse el pago.'}</Text>
          </Card>}
          <Text size="sm" c="dimmed">Pago Mercado Pago: {selected.mercadoPagoPayment?.paymentId || 'Sin ID confirmado'}</Text>
          {NEXT_STATUS[selected.fulfillmentStatus] && !['refunded', 'charged_back'].includes(selected.status) && <Button leftSection={<IconTruckDelivery size={18} />} loading={updating === selected.id} onClick={() => advanceOrder(selected)}>{NEXT_STATUS[selected.fulfillmentStatus].label}</Button>}
        </Stack>}
      </Modal>
    </Container>
  );
}
